<template>
  <div
    ref="results"
    v-click-outside="handleOutsideClick"
    class="status-picker-wrap usn"
    :class="{ 'no-label': !showDropdownLabel }"
    @mousemove="mouseMoveHandle"
    @click.stop="focusInput"
  >
    <input
      v-if="keyboardNavigation"
      :ref="'dropdownInput'"
      v-autofocus
      readonly="true"
      class="hidden-input"
      data-hidden-input="true"
      @click.stop=""
      @keyup.stop="checkKey"
      @keydown.stop="checkDownKey"
    />
    <span
      v-if="showDropdownLabel"
      class="picker-label"
    >
      {{ translate('statusType') }}
    </span>
    <vue-scroll
      ref="searchResultsScroll"
      class="list-statuses-scroll"
      :ops="{
        scrollPanel: { scrollingX: false },
        rail: { gutterOfSide: '4px', gutterOfEnds: '4px', size: '8px' },
        bar: { onlyShowBarOnScroll: false, minSize: 0.25, size: '8px' },
      }"
    >
      <div
        v-for="(status, index) in searchResults"
        :ref="resultRef(index)"
        :key="`${status.label}-${status.list_id}`"
        :data-index="index"
        class="card-status-item pt"
        :class="[
          { selected: isSelectedStatus(status.id, status.list_id) },
          { active: selectedIndex === index },
        ]"
        @click.prevent.stop="handleItemClick(status)"
        @mouseover="mouseOverHandler(index)"
      >
        <em
          ref="listStatusIcon"
          class="custom-icon list-status-icon"
          :style="{
            ...getCustomIconStyle(iconPath(status.icon)),
            backgroundColor: isSelectedStatus(status.id, status.list_id)
              ? (displayColor ?? status.color)
              : status.color,
          }"
        />
        <span class="status-label">{{ status.label }}</span>
        <div class="status-options-wrap">
          <v-popover
            v-if="isSelectedStatus(status.id, status.list_id) && allowColorChange"
            interactive
            trigger="click"
            content-tag="div"
            tag="div"
            :role="'poper'"
            :append-to="$refs.results"
            :on-trigger="(i, e) => e.stopPropagation()"
            :on-untrigger="(i, e) => e.stopPropagation()"
            :placement="'right-end'"
            :offset="[0, 14]"
          >
            <ButtonCustom
              :type="'white border'"
              :variation="'extra-small'"
              class="pt usn color-picker-icon"
              :custom-icon-name="'paint'"
              :custom-icon-size="'16px'"
              :no-label="true"
              @click="focusInput"
            />
            <template #content="{ state, hide }">
              <ColorPickerDropdown
                v-if="state.isVisible"
                :selected-color="displayColor"
                :colors="listStatusColors"
                @colorSelect="handleColorSelect($event, index)"
                @predefinedColorPick="predefinedColorClick($event, hide)"
                @focusChange="focusInput"
              />
            </template>
          </v-popover>
          <em
            v-if="isSelectedStatus(status.id, status.list_id)"
            class="custom-icon check-icon"
            :style="getCustomIconStyle(iconPath('check'))"
          />
        </div>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import cardStatuses from '@/mixins/cardStatuses';
import customIconsMixin from '@/mixins/customIconsMixin';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ColorPickerDropdown from '@/components/elements/ColorPickerDropdown';
import styleVariables from '@/assets/scss/variables.module.scss';
import inputDropdown from '@/mixins/inputDropdown';

export default {
  name: 'ListCardStatusPicker',

  components: {
    ButtonCustom,
    ColorPickerDropdown,
  },

  mixins: [cardStatuses, customIconsMixin, inputDropdown],

  props: {
    selectedStatus: {
      type: Object,
      required: false,
      default: () => ({ color: styleVariables.gray400, id: 'backlog', label: 'Backlog' }),
    },

    customStatusesList: {
      type: Array,
      required: false,
      default: () => [],
    },

    allowColorChange: {
      type: Boolean,
      required: false,
      default: true,
    },

    showDropdownLabel: {
      type: Boolean,
      required: false,
      default: true,
    },

    keyboardNavigation: {
      type: Boolean,
      required: false,
      default: true,
    },

    draftListCreate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['click', 'clickOutside', 'colorSelect', 'esc', 'setHighlighted'],

  data() {
    return {
      filteredOutKeyCodes: ['Space'],
      inputFocusTimeout: null,
      listStatusColors: [
        styleVariables.primary200,
        styleVariables.error300,
        styleVariables.warning300,
        styleVariables.success500,
        styleVariables.primary500,
        styleVariables.bluelight300,
        styleVariables.purple400,
        styleVariables.pink300,
        styleVariables.orange300,
        styleVariables.gray400,
      ],
      displayColor: this.selectedStatus.color,
      changedColorIndex: -1,
      tempColor: this.selectedStatus.color,
    };
  },

  computed: {
    searchResults() {
      const isEpic =
        this.$store.getters.getCurrentRoute.name === this.$constants.epicsBoardRoute.name;
      return this.customStatusesList.length !== 0
        ? this.customStatusesList
        : this.defaultLists(isEpic);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.focusInput();
    });
    if (!this.draftListCreate) {
      this.selectedIndex = this.searchResults.findIndex(
        (s) => s.id === this.selectedStatus.id && s.list_id === this.selectedStatus.listId
      );
    } else {
      this.selectedIndex = 0;
    }
  },

  unmounted() {
    clearTimeout(this.inputFocusTimeout);
  },

  methods: {
    isSelectedStatus(statusId, listId) {
      if (!listId || this.draftListCreate) {
        return statusId === this.selectedStatus.id;
      }
      return (
        !this.draftListCreate &&
        this.customStatusesList.length !== 0 &&
        this.selectedStatus.listId === listId
      );
    },

    handleItemClick(status) {
      const selectedStatus = { ...status };
      if (this.changedColorIndex === this.selectedIndex) {
        selectedStatus.color = this.draftListCreate ? this.selectedStatus.color : this.tempColor;
      }
      this.$emit('click', selectedStatus, this.selectedIndex);
    },

    predefinedColorClick(color, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      this.tempColor = color;
      this.displayColor = color;
      this.focusInput();
    },

    handleOutsideClick() {
      this.$emit('clickOutside');
    },

    handleColorSelect(color, index) {
      this.changedColorIndex = index;
      if (this.$refs.listStatusIcon[index]) {
        this.$refs.listStatusIcon[index].style.backgroundColor = color;
      }
      if (!this.draftListCreate) {
        this.tempColor = color;
      }
      this.$emit('colorSelect', color);
      this.focusInput();
    },

    focusInput(hideFn) {
      if (typeof hideFn === 'function') hideFn();
      clearTimeout(this.inputFocusTimeout);
      this.inputFocusTimeout = setTimeout(() => {
        if (this.$refs.dropdownInput) {
          this.$refs.dropdownInput.focus();
          this.$refs.dropdownInput.select();
        }
      }, 100);
    },

    selectSearchResult(item) {
      this.handleItemClick(item);
    },

    handleEsc(event) {
      this.$emit('esc', event);
    },

    mouseOverHandler(index) {
      if (this.suspendMouseEnter) {
        return;
      }
      this.displayColor = this.draftListCreate ? this.selectedStatus.color : this.tempColor;
      this.selectedIndex = index;
      this.$emit('setHighlighted', index);
    },

    mouseMoveHandle() {
      this.displayColor = this.draftListCreate ? this.selectedStatus.color : this.tempColor;
      this.suspendMouseEnter = false;
    },

    resetSelectedIndex() {
      this.selectedIndex = 0;
    },
  },
};
</script>

<style lang="scss">
.status-picker-wrap {
  display: flex;
  flex-direction: column;
  width: 170px;
  box-shadow:
    0px 2px 16px -4px rgba(16, 24, 40, 0.32),
    0px -2px 16px -4px rgba(16, 24, 40, 0.1);
  border-radius: 8px;
  padding-bottom: 4px;
  left: -8px;
  z-index: 1;
  @apply bg-important-bg border border-solid border-important-border text-text-default relative;

  .list-statuses-scroll {
    display: flex;
    max-height: 300px;
    flex-direction: column;

    .__view {
      flex-direction: column;
    }
  }

  .hidden-input {
    line-height: 0;
    height: 0;
    margin: 0;
    border: none;
    position: absolute;
    outline: none;
  }

  &.no-label {
    padding-top: 4px;
  }

  .picker-label {
    font-size: 12px;
    line-height: 18px;
    text-transform: uppercase;
    margin: 4px 12px;
    @apply text-text-placeholder;
  }

  .card-status-item {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    display: flex;
    padding: 5px 8px 3px 16px;
    align-items: center;
    white-space: nowrap;
    min-height: 32px;
    box-sizing: border-box;
    @apply text-text-default;

    .status-options-wrap {
      display: flex;
      align-items: center;
      margin-left: auto;

      .check-icon {
        opacity: 0;
      }
    }

    .color-picker-icon {
      margin-right: 8px;
      @apply bg-important-bg border-important-border border border-solid;

      em {
        background: var(--iconDefault) !important;
      }
    }

    &.active {
      @apply bg-important-hover;
    }

    &.selected {
      .status-options-wrap {
        .check-icon {
          opacity: 1;
        }
      }
    }

    .custom-icon {
      width: 16px;
      height: 16px;
      display: block;
    }

    .list-status-icon {
      margin-right: 8px;
      background-color: green;
      min-width: 16px;
      min-height: 16px;
    }

    .check-icon {
      background-color: $stBlue;
    }

    .status-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
